import { Component, EventEmitter, computed, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subject } from 'rxjs';
import { AuscultationDataTypes, AuscultationDataTypesEnum } from '../../entity-definitions/auscultation-information.definition';
import { EntityDialogData } from '../../services/entity-dialog.service';
import { AnalysisPostStore } from '../../stores/analysis-post.store';
import { AuscultationInformation, AuscultationInformationStore } from '../../stores/auscultation-info.store';
import { ChoiceListStore } from '../../stores/choice-list.store';
import { LocationStore } from '../../stores/location.store';
import { ManualMeasureStore } from '../../stores/manual-measure.store';
import { AutomaticMeasureStore } from '../../stores/automatic-measure.store';
import { EntityFormValue } from '../../types/entity-form-result.interface';
import { ToastService } from '../../services/toast.service';
import { isConflictWithCode } from '../../utils/http-error.utils';
import { MEASURE_NAME_ALREADY_EXISTS } from '../../utils/measure-error-codes';
import { UniqueNameEntry } from '../pages/assets/configuration/measures/shared/base-measure-info.component';
import { CesDialogFooterComponent } from '../ui/dialog-footer/dialog-footer.component';
import { CesFormDebugPanelComponent } from '../ui/ces-debug-panels/ces-form-debug-panel.component';
import { CesSelectFieldComponent } from '../ui/ces-select-field/ces-select-field.component';
import { CesTextFieldComponent } from '../ui/ces-text-field/ces-text-field.component';

interface AuscultationFormControls {
  name: FormControl<string>;
  type: FormControl<number | null>;
  locationIds: FormControl<number[]>;
  choiceListId: FormControl<number | null>;
  description: FormControl<string>;
  assetId: FormControl<number | null>;
}

type AuscultationStoreApi = {
  update(id: number, data: EntityFormValue<AuscultationInformation>): Promise<unknown>;
  create(data: EntityFormValue<AuscultationInformation>): Promise<unknown>;
};

@Component({
  selector: 'app-auscultation-information-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CesSelectFieldComponent,
    CesTextFieldComponent,
    CesFormDebugPanelComponent,
    CesDialogFooterComponent,
    TranslocoModule,
  ],
  templateUrl: './auscultation-information-form.component.html',
})
export class AuscultationInformationFormComponent implements OnInit, OnDestroy {
  @Input() entity?: AuscultationInformation;
  @Input() parentId?: number;
  @Input() mode: 'create' | 'edit' = 'create';

  @Output() formSubmit = new EventEmitter<EntityFormValue<AuscultationInformation>>();
  @Output() formCancel = new EventEmitter<void>();

  private readonly fb = inject(FormBuilder);
  protected readonly locationsStore = inject(LocationStore);
  protected readonly auscultationInformationStore = inject(AuscultationInformationStore);
  protected readonly choiceListStore = inject(ChoiceListStore);
  protected readonly manualMeasureStore = inject(ManualMeasureStore);
  protected readonly automaticMeasureStore = inject(AutomaticMeasureStore);
  protected readonly analysisPostStore = inject(AnalysisPostStore);
  private readonly translate = inject(TranslocoService);
  private readonly toastService = inject(ToastService);

  readonly existingNamesOnAsset = computed<readonly UniqueNameEntry[]>(() => {
    const assetId = Number(this.parentId);
    if (!Number.isFinite(assetId) || assetId <= 0) return [];
    const sources = [
      ...this.manualMeasureStore.entities(),
      ...this.automaticMeasureStore.entities(),
      ...this.analysisPostStore.entities(),
      ...this.auscultationInformationStore.entities(),
    ];
    const seen = new Set<string>();
    const result: UniqueNameEntry[] = [];
    for (const entity of sources) {
      const record = entity as { assetId?: number; name?: string | null };
      if (record.assetId !== assetId) continue;
      const name = record.name;
      if (!name) continue;
      const key = name.trim().toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      result.push({ name });
    }
    return result;
  });
  private readonly destroy$ = new Subject<void>();
  private readonly dialogConfig = inject<DynamicDialogConfig<EntityDialogData<AuscultationInformation>> | null>(DynamicDialogConfig, {
    optional: true,
  });
  private readonly dialogRef = inject<DynamicDialogRef<AuscultationInformationFormComponent> | null>(DynamicDialogRef, {
    optional: true,
  });

  auscultationForm!: FormGroup<AuscultationFormControls>;
  submitting = false;

  typeOptions: { id: number; name: string }[] = [];

  constructor() {
    this.initForm();
  }

  initForm(): void {
    this.auscultationForm = new FormGroup<AuscultationFormControls>({
      name: this.fb.nonNullable.control(''),
      type: this.fb.control<number | null>(null),
      locationIds: this.fb.nonNullable.control<number[]>([]),
      choiceListId: this.fb.control<number | null>(null),
      description: this.fb.nonNullable.control(''),
      assetId: this.fb.control<number | null>(null, [Validators.required]),
    });
  }

  ngOnInit(): void {
    this.initTypeOptions();
    this.hydrateFromDialogConfig();
    void this.loadAssetRelatedData();
    this.setupFormData();
    this.setupConditionalValidation();
  }

  private initTypeOptions(): void {
    this.typeOptions = [...AuscultationDataTypes].map(v => ({
      id: v.value,
      name: this.translate.translate(v.labelKey),
    }));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private hydrateFromDialogConfig(): void {
    const data = this.dialogConfig?.data;
    if (!data) return;
    if (data.entity !== undefined) this.entity = data.entity;
    if (data.parentId !== undefined) this.parentId = data.parentId;
    this.mode = data.mode === 'edit' ? 'edit' : 'create';
  }

  private setupFormData(): void {
    if (this.parentId) {
      this.auscultationForm.patchValue({ assetId: Number(this.parentId) });
    }

    if (this.isEditMode() && this.entity?.id) {
      this.loadEntityForEdit(this.entity);
      this.auscultationForm.get('type')?.disable();
    }
  }

  private async loadAssetRelatedData(): Promise<void> {
    const assetId = this.auscultationForm.get('assetId')?.value;
    if (!assetId) return;
    // Refresh the four sources used by the unique-name guard so the inline
    // validation mirrors the backend ``validate_name_unique_in_asset`` rule.
    void Promise.all([
      this.locationsStore.getLocationsByAsset(assetId),
      this.manualMeasureStore.getByAssetId(assetId),
      this.automaticMeasureStore.getByAssetId(assetId),
      this.analysisPostStore.getByAssetId(assetId),
      this.auscultationInformationStore.getByAssetId(assetId),
    ]);
  }

  private setupConditionalValidation(): void {
    this.auscultationForm.get('type')?.valueChanges.subscribe(value => {
      const choiceListControl = this.auscultationForm.get('choiceListId');
      if (value === AuscultationDataTypesEnum.CHOICE_LIST) {
        choiceListControl?.setValidators([Validators.required]);
      } else {
        choiceListControl?.clearValidators();
        choiceListControl?.setValue(null);
      }
      choiceListControl?.updateValueAndValidity();
    });
  }

  private loadEntityForEdit(entity: AuscultationInformation): void {
    this.auscultationForm.patchValue({
      name: entity.name ?? '',
      type: entity.type,
      locationIds: entity.locationIds,
      choiceListId: entity.choiceListId,
      description: entity.description ?? '',
      assetId: entity.assetId,
    });
  }

  shouldShowChoiceList(): boolean {
    return this.auscultationForm.get('type')?.value === AuscultationDataTypesEnum.CHOICE_LIST;
  }

  isEditMode(): boolean {
    return this.mode === 'edit';
  }

  markAllAsTouched(): void {
    this.auscultationForm.markAllAsTouched();
    Object.keys(this.auscultationForm.controls).forEach(key => {
      const control = this.auscultationForm.get(key);
      if (control) {
        control.markAsTouched();
        control.updateValueAndValidity();
      }
    });
  }

  buildApiPayload(): EntityFormValue<AuscultationInformation> {
    const raw = this.auscultationForm.getRawValue();
    return {
      type: raw.type,
      assetId: raw.assetId,
      name: raw.name,
      description: raw.description,
      locationIds: raw.locationIds,
      choiceListId: raw.choiceListId ?? undefined,
    } as EntityFormValue<AuscultationInformation>;
  }

  async submitForm(): Promise<void> {
    if (this.auscultationForm.invalid || this.submitting) {
      this.markAllAsTouched();
      return;
    }

    this.submitting = true;
    const apiPayload = this.buildApiPayload();

    try {
      if (this.dialogRef) {
        this.dialogRef.close(apiPayload);
        return;
      }

      this.formSubmit.emit(apiPayload);

      if (!this.formSubmit.observed) {
        const storeApi = this.auscultationInformationStore as unknown as AuscultationStoreApi;
        if (this.mode === 'edit' && this.entity?.id) {
          await storeApi.update(this.entity.id, apiPayload);
        } else {
          await storeApi.create(apiPayload);
        }
      }
    } catch (error) {
      const duplicate = isConflictWithCode(error, MEASURE_NAME_ALREADY_EXISTS);
      const messageKey = duplicate ? 'entities.auscultationInformation.messages.duplicateError' : 'common.errors.saveFailed';
      this.toastService.error(messageKey, undefined, undefined, undefined, { name: apiPayload.name ?? '' });
      console.error(this.translate.translate('common.errors.saveFailed'), error);
    } finally {
      this.submitting = false;
    }
  }

  onFormCancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
      return;
    }
    this.formCancel.emit();
  }
}
